import {
    AsyncProcessStatusMeta,
    Circuit,
    CircuitAsyncStatus,
    CircuitStatus,
    GroupType,
    PesCircuitStatusConfig,
    PesCircuitStatusConfigUtils,
    PesGroup,
    PesGroupUtils,
    Strings
} from "@pes/ui";
import {TranslateService} from '@ngx-translate/core';


export class LbpaCircuitViewUtils {

    /**
     * The timestamp that represents one day.
     */
    private static readonly ONE_DAY: number = 24 * 60 * 60 * 1000;

    /**
     * The timestamp that represents 4 days.
     */
    private static readonly FOUR_DAYS: number = 4 * 24 * 60 * 60 * 1000;

    /**
     * The timestamp that represents seven days.
     */
    private static readonly SEVEN_DAYS: number = 7 * 24 * 60 * 60 * 1000;

    /**
     * Check whether the user is a "responsable".
     *
     * @param {any} features
     * @param {any} delegate
     *
     * @return {boolean} true whether the user is a "responsable"; false otherwise.
     */
    public static isResponsable(features: any, delegate: any): boolean {
        const divisions: PesGroup[] = [];
        PesGroupUtils.flatChildren(features.groups.filter((grp: any) => grp.type === GroupType.DIVISION), divisions);
        if (!features || !features.profiles) {
            return false;
        }
        if (features.profiles.filter((entry: any) => entry.name === 'responsable').length === 0) {
            return false;
        }
        return delegate.groups.filter((grp: any) => divisions.map(div => div.name).indexOf(grp) !== -1).length > 0;
    }

    /**
     * Returns the status icon cresolved from the circuit status.
     *
     * @param {string} status the circuit status.
     * @return {string} the status icon resolved from the circuit status.
     */
    public static getStatusIcon(circuit: Circuit, translate: TranslateService): string {
        const status: CircuitStatus = circuit.dossier.meta.status;
        let cfg: PesCircuitStatusConfig = null;
        switch (status) {
            case CircuitStatus.STARTED:
                cfg = LbpaCircuitViewUtils.resolveStartedStatus(circuit);
                break;
            case CircuitStatus.FINISHED:
                cfg = PesCircuitStatusConfigUtils.FINISHED;
                break;
            case CircuitStatus.REJECTED:
                cfg = PesCircuitStatusConfigUtils.REJECTED;
                break;
            case CircuitStatus.CANCELLED:
                cfg = PesCircuitStatusConfigUtils.CANCELLED;
                break;
            case CircuitStatus.EXPIRED:
                cfg = PesCircuitStatusConfigUtils.EXPIRED;
                break;
            default:
        }
        const title: string = translate.instant('circuit.status.' + cfg.key);
        const border: string = `border border-${cfg.style.substr(6)}`;
        return `<span class="circuit-status-icon ${cfg.style} ${border} d-inline-block align-middle mr-1">
    <i title="${title}" class="fa ${cfg.icon}"></i>
</span>`;
    }

    /**
     * Resolves and returns the config of the specified circuit when the circuit is started.
     *
     * @param circuit the circuit for which to get the config.
     */
    public static resolveStartedStatus(circuit: Circuit): PesCircuitStatusConfig {
        let result: PesCircuitStatusConfig = PesCircuitStatusConfigUtils.STARTED;
        const asyncProcessStatus: any = circuit.dossier.meta.asyncProcessStatus;
        if (asyncProcessStatus) {
            const asyncProcessStatusMeta: AsyncProcessStatusMeta = asyncProcessStatus;
            if (asyncProcessStatusMeta.status === CircuitAsyncStatus.ASYNC_PROCESS_PENDING) {
                const date: Date = new Date(asyncProcessStatusMeta.updatedDate);
                const offset: number = Date.now() - date.getTime();
                const min: number = Math.floor((offset / 1000 / 60) << 0);
                result = min >= 5 ? PesCircuitStatusConfigUtils.ASYNC_PROCESS_ERROR
                    : PesCircuitStatusConfigUtils.ASYNC_PROCESS_PENDING;

            }
        }
        return result;
    }

    /**
     * Returns a string that represents the expiration flag whether the specified circuit expires soon.
     *
     * @param {Circuit} circuit the reference to the circuit for which to get the expiration soon flag.
     * @return {string} a string that represents the expiration flag whether the specified circuit expires soon.
     */
    public static getExpirationFlag(circuit: Circuit, translate: TranslateService): string {
        let icon: string = Strings.EMPTY_STRING;
        let title: string = Strings.EMPTY_STRING;
        if (circuit.dossier.meta.status === CircuitStatus.STARTED) {
            const expirationDate: string = circuit.dossier.meta.expirationDate;
            if (expirationDate) {
                const rest: number = Date.parse(expirationDate) - Date.now();
                if (rest <= LbpaCircuitViewUtils.FOUR_DAYS) {
                    title = translate.instant('bo_circuits.list.expires_close');
                    icon =
                        `<i aria-hidden="true" class="fa fa-flag text-danger d-inline-block align-middle circuit-expiration-flag" title="${title}"></i>`;
                } else if (rest <= LbpaCircuitViewUtils.SEVEN_DAYS) {
                    title = translate.instant('bo_circuits.list.expires_soon');
                    icon =
                        `<i aria-hidden="true" class="fa fa-flag text-warning d-inline-block align-middle circuit-expiration-flag" title="${title}"></i>`;
                }
            }
        }
        return icon;
    }
}



// WEBPACK FOOTER //
// ./src/app/utils/lbpa-circuit-view.utils.ts